import { z } from 'zod';
import { lenientString } from '../schema-extensions';
import { ContactNode } from './contact-node-schema';

// ============================================================================
// CONSTANTS
// ============================================================================
const PROTECTION_CONTACT_CONSTANTS = {
  CLAIM_PHONE_MAX_LENGTH: 20,
} as const;

// ============================================================================
// LENIENT SCHEMA (for deserialization)
// ============================================================================
const lenientProtectionContactNodeSchema = z.object({
  policyPhoneNumber: lenientString(ContactNode.Constants.PHONE_MAX_LENGTH, ''),
  webUrl: lenientString(ContactNode.Constants.WEB_URL_MAX_LENGTH, ''),
  email: lenientString(ContactNode.Constants.EMAIL_MAX_LENGTH, ''),
  username: lenientString(ContactNode.Constants.USERNAME_MAX_LENGTH, ''),
  claimPhoneNumber: lenientString(PROTECTION_CONTACT_CONSTANTS.CLAIM_PHONE_MAX_LENGTH, ''),
}).transform(val => ({
  policyPhoneNumber: val.policyPhoneNumber,
  webUrl: val.webUrl,
  email: val.email,
  username: val.username,
  claimPhoneNumber: val.claimPhoneNumber,
}));

// ============================================================================
// STRICT SCHEMA (for validation)
// ============================================================================
const strictProtectionContactNodeSchema = z.object({
  policyPhoneNumber: z.string()
    .max(ContactNode.Constants.PHONE_MAX_LENGTH, `Phone number must be ${ContactNode.Constants.PHONE_MAX_LENGTH} characters or less`)
    .optional()
    .transform(val => val ?? ''),
  webUrl: z.union([
    z.string().url('Must be a valid URL'),
    z.literal(''),
    z.undefined()
  ])
    .optional()
    .transform(val => val ?? ''),
  email: z.union([
    z.string().email('Must be a valid email'),
    z.literal(''),
    z.undefined()
  ])
    .optional()
    .transform(val => val ?? ''),
  username: z.string()
    .max(ContactNode.Constants.USERNAME_MAX_LENGTH, `Username must be ${ContactNode.Constants.USERNAME_MAX_LENGTH} characters or less`)
    .optional()
    .transform(val => val ?? ''),
  claimPhoneNumber: z.string()
    .max(PROTECTION_CONTACT_CONSTANTS.CLAIM_PHONE_MAX_LENGTH, `Claim phone number must be ${PROTECTION_CONTACT_CONSTANTS.CLAIM_PHONE_MAX_LENGTH} characters or less`)
    .optional()
    .transform(val => val ?? ''),
}).transform(val => ({
  policyPhoneNumber: val.policyPhoneNumber,
  webUrl: val.webUrl,
  email: val.email,
  username: val.username,
  claimPhoneNumber: val.claimPhoneNumber,
}));

// ============================================================================
// TYPE
// ============================================================================
export type ProtectionContactNode = z.infer<typeof lenientProtectionContactNodeSchema>;

// ============================================================================
// NAMESPACE (for Constants and Methods)
// ============================================================================
export namespace ProtectionContactNode {
  export const Constants = PROTECTION_CONTACT_CONSTANTS;

  export function deserialize(data: unknown): ProtectionContactNode {
    return lenientProtectionContactNodeSchema.parse(data);
  }

  export function create(data: unknown): ProtectionContactNode {
    return strictProtectionContactNodeSchema.parse(data);
  }
}
